// File: src/components/HeroCarousel.jsx
// Main carousel with promotional hero slides
// Includes product discovery cards below carousel (using real API data)

import React, { useState, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import HeroCategoryCardsSection from './HeroCategoryCardsSection';
import { FetchNestedCategories } from '../Data';
import { transformCategoriesToHeroCards } from '../utils/heroCategoryTransformer';
import { title } from 'process';

const HeroCarousel = ({ slides = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [heroCategories, setHeroCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const autoPlayRef = useRef(null);

  // Fetch and transform categories for hero cards
  useEffect(() => {
    const loadHeroCategories = async () => {
      try {
        setCategoriesLoading(true);
        const apiCategories = await FetchNestedCategories();
        const transformedCards = transformCategoriesToHeroCards(apiCategories);
        setHeroCategories(transformedCards);
      } catch (error) {
        console.error('Failed to load hero categories:', error);
        setHeroCategories([]); // Empty array on error
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadHeroCategories();
  }, []);

  const defaultSlides = [
    {
      id: 1,
      // title: 'Save 40% on Bulk Orders',
      title: '',
      // subtitle: 'Order 100+ units and unlock exclusive pricing',
      subtitle: '',
      image: 'https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/2be6674a-a4ef-4f47-bee2-61a04288f70f.png',
      cta: 'Shop Bulk Deals',
      ctaLink: '/bulk-orders',
      bgColor: 'red',
    },
    {
      id: 2,
      title: '',
      subtitle: '',
      image: 'https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/1876cd78-f019-4bc8-9ebe-8e7a8efbd0a1.png',
      cta: '',
      ctaLink: '/products',
      bgColor: 'from-purple-600 to-purple-800',
    },
    {
      id: 3,
      title: '',
      subtitle: '',
      image: 'https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/946934a0-ee6a-4dfc-b268-378c49072fe2.png',
      cta: 'Learn More',
      ctaLink: '/about',
      bgColor: 'from-green-600 to-green-800',
    },
  ];

  const displaySlides = slides.length > 0 ? slides : defaultSlides;

  useEffect(() => {
    if (!isAutoPlaying || isHovered) return;

    autoPlayRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % displaySlides.length);
    }, 5000);

    return () => clearInterval(autoPlayRef.current);
  }, [isAutoPlaying, isHovered, displaySlides.length]);

  const goToSlide = (index) => setCurrentSlide(index);
  const goToPrevious = () =>
    setCurrentSlide((prev) => (prev - 1 + displaySlides.length) % displaySlides.length);
  const goToNext = () => setCurrentSlide((prev) => (prev + 1) % displaySlides.length);

  return (
    <div className="w-full">
      {/* Main Hero Carousel */}
      <div
        className="relative w-full overflow-hidden mb-0 group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Amazon-style compact hero: 220px mobile, 280px tablet, 350px desktop */}
        <div className="relative h-[220px] sm:h-[280px] lg:h-[350px] overflow-hidden">
          {displaySlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {/* High-quality image rendering - no overlay for crisp banners */}
              {slide.image && (
                <img
                  src={slide.image}
                  alt={slide.title || `Hero banner ${index + 1}`}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  draggable="false"
                />
              )}

              {/* Gradient Fallback */}
              {!slide.image && (
                <div className={`absolute inset-0 bg-gradient-to-r ${slide.bgColor}`}></div>
              )}

              {/* Content */}
              <div className="absolute inset-0 flex items-center justify-start px-6 md:px-12">
                <div className="max-w-2xl text-white">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 leading-tight">
                    {slide.title}
                  </h1>
                  <p className="text-lg sm:text-xl text-gray-100 mb-6">{slide.subtitle}</p>
                  <a
                    href={slide.ctaLink}
                    className="inline-block bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold py-3 px-6 rounded-full transition-colors duration-300 shadow-lg"
                  >
                    {slide.cta}
                  </a>
                </div>
              </div>
            </div>
          ))}

          {/* Navigation Arrows - Amazon style: subtle, hover on desktop, always visible on mobile */}
          <button
            onClick={goToPrevious}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-gray-800 p-2 sm:p-2.5 rounded opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300"
            aria-label="Previous slide"
          >
            <FaChevronLeft className="text-base sm:text-lg" />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-gray-800 p-2 sm:p-2.5 rounded opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300"
            aria-label="Next slide"
          >
            <FaChevronRight className="text-base sm:text-lg" />
          </button>

          {/* Slide Indicators - Minimal and clean */}
          <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
            {displaySlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentSlide ? 'bg-gray-800 w-6' : 'bg-gray-400/60 w-1.5 hover:bg-gray-600'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Product Discovery Cards - Below Hero Carousel (Real API Data) */}
      {!categoriesLoading && heroCategories.length > 0 && (
        <HeroCategoryCardsSection categories={heroCategories} />
      )}
      
      {/* Loading state for category cards */}
      {categoriesLoading && (
        <div className="w-full bg-white px-4 py-8 border-t border-gray-100">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-gray-100 rounded-md h-80 animate-pulse"></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroCarousel;